import { LEVELS, ONSITE_INFO, SANDBOX, type LevelDef, type OnsiteTheme } from '../core/levels';
import { clock, exportRun, type RunReport } from '../core/report';
import type { World } from '../core/world';
import { sfx } from '../audio/sfx';
import { loadProgress, rank, type Settings } from '../game/progress';
import { h } from './dom';

const KIND_LABEL = { speed: 'Speed', safety: 'Safety', agency: 'Agency', framing: 'Framing' } as const;

function starRows(level: LevelDef, got?: [boolean, boolean, boolean]) {
  const g = got ?? [false, false, false];
  const row = (i: number, text: string, kind?: 'speed' | 'safety' | 'agency' | 'framing') =>
    h('div', { class: `star-row${g[i] ? ' got' : ''}` }, h('span', { class: 's', text: g[i] ? '★' : '☆' }), h('span', { text }), kind ? h('span', { class: `tag ${kind}`, text: KIND_LABEL[kind] }) : h('span', { class: 'tag', text: 'Goal' }));
  const goal = level.goal.type === 'ship' ? `Ship ${level.goal.count} good releases` : `Hold ${level.goal.perMin} releases a minute for ${level.goal.hold}s`;
  return h('div', { class: 'stars' }, row(0, goal), row(1, level.stars[0].text, level.stars[0].kind), row(2, level.stars[1].text, level.stars[1].kind));
}

export const CAMPAIGN_IDS = [...LEVELS.map((l) => l.id), ONSITE_INFO.id];

function rankLine() {
  const r = rank(CAMPAIGN_IDS);
  // The bar shows campaign progress: stars earned out of all stars.
  const pct = Math.round((r.stars / Math.max(1, r.total)) * 100);
  return h(
    'div',
    { class: 'rank' },
    h('div', {}, h('b', { text: r.name }), h('span', { text: ` · ${r.stars} of ${r.total} stars${r.next ? ` · ${r.next.at - r.stars} more for ${r.next.name}` : ''}` })),
    h('div', { class: 'bar', style: { maxWidth: '360px' } }, h('i', { style: { width: `${Math.min(100, Math.max(2, pct))}%`, background: 'var(--gold)' } })),
  );
}

export function titleScreen(opts: { onPlay(): void; onLevels(): void; onSandbox(): void; onSettings(): void; onWatch(file: File): void; nextName: string }) {
  const file = h('input', { type: 'file', accept: 'application/json,.json', style: { display: 'none' }, onchange: (e: Event) => { const f = (e.target as HTMLInputElement).files?.[0]; if (f) opts.onWatch(f); } }) as HTMLInputElement;
  return h(
    'div',
    { class: 'screen title-bg' },
    h(
      'div',
      { class: 'wrap title-screen' },
      h('h1', { class: 'logo' }, h('span', { class: 'ring', 'aria-hidden': 'true' }), 'Blast Radius'),
      h('p', { class: 'tagline', text: 'A factory game about shipping without permission. Your agents are fast and sometimes wrong. Act without asking, and keep what breaks small.' }),
      h(
        'div',
        { class: 'title-actions' },
        h('button', { class: 'btn primary', onclick: () => { sfx.unlock(); opts.onPlay(); }, text: `Play: ${opts.nextName}` }),
        h('button', { class: 'btn', onclick: () => { sfx.unlock(); opts.onLevels(); }, text: 'Levels' }),
        h('button', { class: 'btn', onclick: () => { sfx.unlock(); opts.onSandbox(); }, text: 'Sandbox' }),
        h('button', { class: 'btn ghost', onclick: () => file.click(), text: 'Watch a run' }),
        h('button', { class: 'btn ghost', onclick: () => opts.onSettings(), text: 'Settings' }),
        file,
      ),
      rankLine(),
      h('p', { class: 'title-foot', text: 'Everything you do is recorded. At the end of each run you get a report on how you moved and what broke. Talk to your copilot with V, or type commands with Enter.' }),
    ),
  );
}

export function levelsScreen(opts: { onPick(l: LevelDef): void; onOnsite(): void; onBack(): void }) {
  const progress = loadProgress();
  const onsiteCard = () => {
    const p = progress[ONSITE_INFO.id];
    const got = p?.stars ?? [false, false, false];
    const row = (i: number, text: string, kind?: 'framing') =>
      h('div', { class: `star-row${got[i] ? ' got' : ''}` }, h('span', { class: 's', text: got[i] ? '\u2605' : '\u2606' }), h('span', { text }), h('span', { class: `tag ${kind ?? ''}`, text: kind ? 'Framing' : 'Goal' }));
    return h(
      'button',
      { class: 'card panel', onclick: () => { sfx.unlock(); opts.onOnsite(); } },
      h('div', { class: 'card-head' }, h('h3', { text: ONSITE_INFO.name }), h('span', { class: 'card-num', text: `${ONSITE_INFO.index} of ${LEVELS.length + 1}` })),
      h('p', { text: ONSITE_INFO.premise }),
      h('div', { class: 'stars' }, row(0, 'Finish all three problems'), row(1, ONSITE_INFO.stars[0].text, 'framing'), row(2, ONSITE_INFO.stars[1].text, 'framing')),
    );
  };
  const card = (l: LevelDef) => {
    const p = progress[l.id];
    return h(
      'button',
      { class: 'card panel', onclick: () => { sfx.unlock(); opts.onPick(l); } },
      h('div', { class: 'card-head' }, h('h3', { text: l.name }), h('span', { class: 'card-num', text: l.index <= LEVELS.length ? `${l.index} of ${LEVELS.length + 1}` : 'Free play' })),
      h('p', { text: l.premise }),
      starRows(l, p?.stars),
      p?.bestSeconds ? h('span', { class: 'tag', style: { justifySelf: 'start' }, text: `Best ${clock(p.bestSeconds)}` }) : null,
    );
  };
  return h(
    'div',
    { class: 'screen' },
    h(
      'div',
      { class: 'wrap' },
      h('h2', { text: 'Levels' }),
      h('p', { class: 'lede', text: 'Each level adds one kind of pressure. Every level is open; they read best in order.' }),
      rankLine(),
      h('div', { class: 'grid' }, LEVELS.map(card), onsiteCard(), card(SANDBOX)),
      h('div', { class: 'report-actions' }, h('button', { class: 'btn', onclick: opts.onBack, text: 'Back' })),
    ),
  );
}

export function introScreen(level: LevelDef, opts: { onStart(): void; onBack(): void }) {
  const progress = loadProgress()[level.id];
  const first = level.index === 1;
  return h(
    'div',
    { class: 'screen clear' },
    h(
      'div',
      { class: 'intro' },
      h(
        'div',
        { class: 'intro-card panel' },
        h('div', { class: 'card-head' }, h('h2', { text: level.name }), h('span', { class: 'card-num', text: level.index <= LEVELS.length ? `Level ${level.index}` : 'Sandbox' })),
        h('p', { class: 'premise', text: level.premise }),
        level.introduces.length ? h('ul', {}, level.introduces.map((t) => h('li', { text: t }))) : null,
        starRows(level, progress?.stars),
        h('div', { class: 'intro-goal' }, level.timeLimit ? h('span', { text: `Clock: ${clock(level.timeLimit)}. Trust at zero ends the run.` }) : h('span', { text: 'No clock. Trust at zero ends the run.' })),
        first
          ? h(
              'div',
              { class: 'keys' },
              h('div', {}, h('kbd', { text: '1-4' }), ' pick a building'),
              h('div', {}, h('kbd', { text: 'Click' }), ' place; drag belts'),
              h('div', {}, h('kbd', { text: 'R' }), ' rotate'),
              h('div', {}, h('kbd', { text: 'Right-click' }), ' remove'),
              h('div', {}, h('kbd', { text: 'Drag' }), ' move the map'),
              h('div', {}, h('kbd', { text: 'H' }), ' all controls'),
            )
          : null,
        h('div', { class: 'report-actions' }, h('button', { class: 'btn primary', onclick: () => { sfx.unlock(); opts.onStart(); }, text: 'Start' }), h('button', { class: 'btn ghost', onclick: opts.onBack, text: 'Back' })),
      ),
    ),
  );
}

export function settingsScreen(settings: Settings, voice: { gptLive: boolean; reason?: string; passphrase?: boolean } | null, opts: { onChange(s: Settings): void; onBack(): void }) {
  const s = { ...settings };
  const save = () => opts.onChange({ ...s });
  const toggle = (label: string, sub: string, get: () => boolean, set: (v: boolean) => void) => {
    const input = h('input', { type: 'checkbox', checked: get() || undefined, onchange: (e: Event) => { set((e.target as HTMLInputElement).checked); save(); } });
    return h('label', { class: 'setting' }, h('div', {}, h('b', { text: label }), h('span', { text: sub })), input);
  };
  const vol = h('input', { type: 'range', min: '0', max: '1', step: '0.05', value: String(sfx.volume), oninput: (e: Event) => sfx.setVolume(Number((e.target as HTMLInputElement).value)) });
  const mode = h(
    'select',
    { onchange: (e: Event) => { s.voiceMode = (e.target as HTMLSelectElement).value as Settings['voiceMode']; save(); } },
    h('option', { value: 'auto', selected: s.voiceMode === 'auto' || undefined, text: 'Best available' }),
    h('option', { value: 'gpt-live', selected: s.voiceMode === 'gpt-live' || undefined, text: 'GPT-Live 1' }),
    h('option', { value: 'browser', selected: s.voiceMode === 'browser' || undefined, text: 'Browser speech' }),
  );
  const pass = h('input', { type: 'password', placeholder: 'Only if the host set one', value: s.voicePass, onchange: (e: Event) => { s.voicePass = (e.target as HTMLInputElement).value; save(); } });
  const status = voice ? (voice.gptLive ? 'GPT-Live is available on this server.' : `GPT-Live is off: ${voice.reason ?? 'no key on the server'}. Browser speech still works in Chrome.`) : 'Checking voice…';
  return h(
    'div',
    { class: 'screen' },
    h(
      'div',
      { class: 'wrap', style: { maxWidth: '640px' } },
      h('h2', { text: 'Settings' }),
      h(
        'div',
        { class: 'settings' },
        toggle('Reduce motion', 'No screen shake, fewer particles, instant transitions.', () => s.reducedMotion, (v) => (s.reducedMotion = v)),
        toggle('Always show labels', 'Helpful when you build by voice.', () => s.labels, (v) => (s.labels = v)),
        toggle('Sound', 'Mute with M during play.', () => !sfx.muted, (v) => sfx.setMuted(!v)),
        h('label', { class: 'setting' }, h('div', {}, h('b', { text: 'Volume' })), vol),
        h('label', { class: 'setting' }, h('div', {}, h('b', { text: 'Voice' }), h('span', { text: status })), mode),
        voice?.passphrase ? h('label', { class: 'setting' }, h('div', {}, h('b', { text: 'Voice passphrase' }), h('span', { text: 'This host asks for one before it starts a GPT-Live session.' })), pass) : null,
      ),
      h('div', { class: 'report-actions' }, h('button', { class: 'btn primary', onclick: opts.onBack, text: 'Done' })),
    ),
  );
}

// ---------------------------------------------------------------- report

function svg<K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string | number>, ...kids: Node[]) {
  const el = document.createElementNS('http://www.w3.org/2000/svg', tag);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, String(v));
  el.append(...kids);
  return el;
}

function quadrant(report: RunReport, history: { agency: number; safety: number }[], reduced: boolean) {
  const S = 320;
  const pad = 34;
  const X = (a: number) => pad + (a / 100) * (S - pad * 2);
  const Y = (s: number) => S - pad - (s / 100) * (S - pad * 2);
  const g = svg('svg', { viewBox: `0 0 ${S} ${S}`, class: 'quad', role: 'img', 'aria-label': `Agency ${report.agency}, safety ${report.safety}: ${report.archetype.name}` });
  g.append(svg('rect', { x: X(50), y: Y(100), width: X(100) - X(50), height: Y(50) - Y(100), fill: 'rgba(89,217,142,0.07)' }));
  g.append(svg('rect', { x: pad, y: pad, width: S - pad * 2, height: S - pad * 2, fill: 'none', stroke: 'rgba(160,180,210,0.2)', rx: 6 }));
  g.append(svg('line', { x1: X(50), y1: pad, x2: X(50), y2: S - pad, stroke: 'rgba(160,180,210,0.18)', 'stroke-dasharray': '4 4' }));
  g.append(svg('line', { x1: pad, y1: Y(50), x2: S - pad, y2: Y(50), stroke: 'rgba(160,180,210,0.18)', 'stroke-dasharray': '4 4' }));
  const label = (x: number, y: number, text: string, anchor = 'middle', fill = 'rgba(200,215,235,0.55)') => {
    const t = svg('text', { x, y, 'text-anchor': anchor, fill, 'font-size': 12, 'font-family': 'inherit' });
    t.textContent = text;
    g.append(t);
  };
  // Quadrant names sit outside the plot so a dot never covers them.
  label(X(75), pad - 10, 'AI-native', 'middle', 'rgba(156,242,192,0.95)');
  label(X(25), pad - 10, 'Careful');
  label(X(75), S - pad + 18, 'Cowboy');
  label(X(25), S - pad + 18, 'Passenger');
  const yl = svg('text', { x: 14, y: S / 2, 'text-anchor': 'middle', fill: 'rgba(200,215,235,0.45)', 'font-size': 11, transform: `rotate(-90 14 ${S / 2})` });
  yl.textContent = 'Safety \u2192';
  g.append(yl);
  const xl = svg('text', { x: S - pad, y: Y(50) - 6, 'text-anchor': 'end', fill: 'rgba(200,215,235,0.4)', 'font-size': 11 });
  xl.textContent = 'Agency \u2192';
  g.append(xl);
  for (const r of history.slice(-8, -1)) g.append(svg('circle', { cx: X(r.agency), cy: Y(r.safety), r: 4, fill: 'rgba(160,180,210,0.35)' }));
  const dot = svg('circle', { cx: X(50), cy: Y(50), r: 8, fill: '#E8EEF5', stroke: '#080B10', 'stroke-width': 3 });
  g.append(dot);
  const place = () => {
    dot.setAttribute('cx', String(X(report.agency)));
    dot.setAttribute('cy', String(Y(report.safety)));
  };
  if (reduced) place();
  else {
    dot.style.transition = 'cx 0.7s cubic-bezier(0.2,0.7,0.2,1), cy 0.7s cubic-bezier(0.2,0.7,0.2,1)';
    requestAnimationFrame(() => requestAnimationFrame(place));
  }
  return g;
}

function timeline(w: World) {
  const snaps = w.snapshots;
  const W = 600;
  const H = 90;
  const g = svg('svg', { viewBox: `0 0 ${W} ${H}`, class: 'timeline', preserveAspectRatio: 'none', role: 'img', 'aria-label': 'Releases per minute and trust over the run' });
  if (snaps.length < 2) return g;
  const maxRate = Math.max(10, ...snaps.map((s) => s.rate));
  const T = snaps[snaps.length - 1].t || 1;
  const pts = (f: (s: (typeof snaps)[number]) => number) => snaps.map((s) => `${((s.t / T) * W).toFixed(1)},${(H - 4 - f(s) * (H - 8)).toFixed(1)}`).join(' ');
  g.append(svg('polyline', { points: pts((s) => s.trust / 100), fill: 'none', stroke: 'rgba(127,214,255,0.7)', 'stroke-width': 2 }));
  g.append(svg('polyline', { points: pts((s) => s.rate / maxRate), fill: 'none', stroke: 'rgba(156,242,192,0.9)', 'stroke-width': 2 }));
  for (const i of w.incidents) {
    if (i.cause === 'flawed-release') continue;
    const x = ((i.t / 30 / T) * W).toFixed(1);
    g.append(svg('line', { x1: x, y1: 0, x2: x, y2: H, stroke: i.contained ? 'rgba(127,214,255,0.25)' : 'rgba(255,61,206,0.5)', 'stroke-width': 1 }));
  }
  return g;
}

export function reportScreen(report: RunReport, w: World, reduced: boolean, opts: { onRetry(): void; onNext(): void; onLevels(): void; onReplay(): void; hasNext: boolean }) {
  const m = report.metrics;
  const won = m.outcome === 'won';
  const hist = loadProgress()[w.level.id]?.runs ?? [];
  const stat = (v: string | number, label: string) => h('div', {}, h('b', { text: String(v) }), h('span', { text: label }));
  const download = () => {
    const blob = new Blob([JSON.stringify(exportRun(w, report), null, 2)], { type: 'application/json' });
    const a = h('a', { href: URL.createObjectURL(blob), download: `blast-radius-${w.level.id}-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.json` });
    document.body.append(a);
    a.click();
    a.remove();
  };
  return h(
    'div',
    { class: 'screen' },
    h(
      'div',
      { class: 'wrap' },
      h(
        'div',
        { class: 'report' },
        h(
          'section',
          { class: 'panel' },
          h('p', { class: 'outcome', text: won ? 'Shipped.' : 'Run over.' }),
          h('p', { class: 'outcome-reason', text: `${w.level.name}. ${m.reason} ${clock(m.seconds)}.` }),
          h('div', { style: { marginTop: '14px' } }, starRows(w.level, report.stars)),
          report.framing.length ? h('p', { class: 'section-title', text: 'How you framed problems' }) : null,
          report.framing.length ? h('ul', { class: 'evidence' }, report.framing.map((t) => h('li', { text: t }))) : null,
          h(
            'div',
            { class: 'statgrid' },
            stat(m.goodReleases, 'good releases'),
            stat(m.peakRate, 'peak per minute'),
            stat(`${m.trustEnd}`, `trust at the end (low ${m.trustMin})`),
            stat(m.incidents - m.flawedShipped, 'incidents'),
            stat(m.maxBlast, 'worst blast radius'),
            stat(m.incidentsContained, 'contained'),
            stat(m.asks, `times you asked (${clock(m.waitSeconds)} waiting)`),
            stat(m.acts, 'times you just did it'),
            stat(`${Math.round(m.explored * 100)}%`, 'of the map explored'),
            stat(`${m.secretsFound}/${m.secretsTotal}`, 'secrets found'),
            stat(`${Math.round(m.delegationShare * 100)}%`, 'built by something else'),
            stat(m.firstBuildSec < 0 ? '—' : clock(m.firstBuildSec), 'to your first build'),
          ),
          timeline(w),
          h('div', { class: 'legend' }, h('span', { class: 'lg rate', text: 'Releases per minute' }), h('span', { class: 'lg trust', text: 'Trust' }), h('span', { class: 'lg inc', text: 'Incidents' })),
          report.moments.length ? h('ul', { class: 'moments' }, report.moments.map((t) => h('li', { text: t }))) : null,
        ),
        h(
          'section',
          { class: 'panel' },
          quadrant(report, hist, reduced),
          h('div', { class: 'arch' }, h('b', { text: report.archetype.name }), h('p', { text: report.archetype.line })),
          h('div', { class: 'scores' }, h('div', { class: 'score' }, h('b', { text: String(report.agency) }), h('span', { text: 'Agency' })), h('div', { class: 'score' }, h('b', { text: String(report.safety) }), h('span', { text: 'Safety' }))),
          h(
            'div',
            { class: 'report-actions' },
            won && opts.hasNext ? h('button', { class: 'btn primary', onclick: opts.onNext, text: 'Next level' }) : null,
            h('button', { class: won && opts.hasNext ? 'btn' : 'btn primary', onclick: opts.onRetry, text: 'Try again' }),
            h('button', { class: 'btn', onclick: opts.onLevels, text: 'Levels' }),
            h('button', { class: 'btn', onclick: opts.onReplay, text: 'Watch replay' }),
            h('button', { class: 'btn ghost', onclick: download, text: 'Download run log' }),
          ),
        ),
      ),
    ),
  );
}

// ---------------------------------------------------------------- the onsite

export function onsitePickScreen(themes: OnsiteTheme[], opts: { onPick(id: string): void; onBack(): void }) {
  return h(
    'div',
    { class: 'screen' },
    h(
      'div',
      { class: 'wrap', style: { maxWidth: '860px' } },
      h('h2', { text: ONSITE_INFO.name }),
      h('p', { class: 'lede', text: 'Three problems, released one at a time. The first gets 8 minutes, the second 4. The third gets 2, which is too short to build anything, so you plan it and we simulate the plan. Pick the problem closest to your strengths to go first.' }),
      h(
        'div',
        { class: 'grid' },
        themes.map((t) =>
          h('button', { class: 'card panel', onclick: () => { sfx.unlock(); opts.onPick(t.id); } }, h('h3', { text: t.name }), h('p', { text: t.pitch }), h('span', { class: 'tag', style: { justifySelf: 'start' }, text: 'Go first with this' })),
        ),
      ),
      h('div', { class: 'report-actions' }, h('button', { class: 'btn', onclick: opts.onBack, text: 'Back' })),
    ),
  );
}

export function onsiteBreakScreen(part: number, last: { name: string; won: boolean; reason: string } | null, next: LevelDef, opts: { onStart(): void }) {
  const minutes = Math.round(next.timeLimit / 60);
  return h(
    'div',
    { class: 'screen clear' },
    h(
      'div',
      { class: 'intro' },
      h(
        'div',
        { class: 'intro-card panel' },
        last ? h('p', { class: `verdict ${last.won ? 'good' : 'bad'}`, style: { margin: '0' }, text: `${last.name}: ${last.won ? 'solved' : 'not solved'}. ${last.reason}` }) : null,
        h('div', { class: 'card-head' }, h('h2', { text: `Problem ${part} of 3` }), h('span', { class: 'card-num', text: `${minutes} minute${minutes === 1 ? '' : 's'}` })),
        h('p', { class: 'premise', text: next.premise }),
        h('p', { class: 'hint', text: next.flags.planOnly ? 'You can test the plan as often as you like. At the deadline, the plan itself gets simulated for three minutes.' : 'Nobody will tell you what’s wrong. You’ll see symptoms, not causes.' }),
        h('div', { class: 'report-actions' }, h('button', { class: 'btn primary', onclick: () => { sfx.unlock(); opts.onStart(); }, text: 'Start the clock' })),
      ),
    ),
  );
}

export function onsiteReportScreen(
  parts: { level: LevelDef; report: RunReport; world: World }[],
  stars: [boolean, boolean, boolean],
  opts: { onRetry(): void; onLevels(): void },
) {
  const row = (i: number, text: string) =>
    h('div', { class: `star-row${stars[i] ? ' got' : ''}` }, h('span', { class: 's', text: stars[i] ? '★' : '☆' }), h('span', { text }), h('span', { class: `tag ${i ? 'framing' : ''}`, text: i ? 'Framing' : 'Goal' }));
  return h(
    'div',
    { class: 'screen' },
    h(
      'div',
      { class: 'wrap' },
      h('p', { class: 'outcome', text: 'Onsite done.' }),
      h('div', { class: 'stars', style: { marginTop: '12px', maxWidth: '560px' } }, row(0, 'Finish all three problems'), row(1, ONSITE_INFO.stars[0].text), row(2, ONSITE_INFO.stars[1].text)),
      h(
        'div',
        { class: 'grid', style: { marginTop: '18px' } },
        parts.map((p, i) =>
          h(
            'section',
            { class: 'card panel' },
            h('div', { class: 'card-head' }, h('h3', { text: `${i + 1}. ${p.level.name}` }), h('span', { class: `tag ${p.world.outcome === 'won' ? 'safety' : ''}`, text: p.world.outcome === 'won' ? 'Solved' : 'Not solved' })),
            h('p', { text: `${clock(p.report.metrics.seconds)} of ${clock(p.level.timeLimit)}. ${p.report.metrics.reason}` }),
            p.report.framing.length ? h('ul', { class: 'evidence' }, p.report.framing.map((t) => h('li', { text: t }))) : null,
          ),
        ),
      ),
      h('div', { class: 'report-actions' }, h('button', { class: 'btn primary', onclick: opts.onRetry, text: 'Do the onsite again' }), h('button', { class: 'btn', onclick: opts.onLevels, text: 'Levels' })),
    ),
  );
}
